import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';
import {
  extractAuthorizationCode,
  exchangeCodeForTokens
} from '../../services/facebookOAuth';
import PageSelector from '../../components/PageSelector/PageSelector';
import { SEO_AUDIT_BASE } from '../../utils/constants';

const FacebookCallback = () => {
  const history = useHistory();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState(
    'Processing Facebook authorization...'
  );
  const [clientId, setClientId] = useState(null);
  const [connectionType, setConnectionType] = useState('facebook');
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    handleCallback();
  }, [history]);

  const handleCallback = async () => {
    try {
      // Extraer code y state de la URL
      const result = extractAuthorizationCode();

      if (!result.success) {
        setStatus('error');
        setMessage(result.error || 'Authorization failed');
        setTimeout(() => {
          if (window.opener && !window.opener.closed) {
            window.close();
          } else {
            history.push(`/client/${result.clientId || ''}`);
          }
        }, 3000);
        return;
      }

      const {
        code,
        clientId: extractedClientId,
        connectionType: extractedConnectionType
      } = result;
      setClientId(extractedClientId);
      setConnectionType(extractedConnectionType || 'facebook');

      console.log('Authorization code received, exchanging for tokens...');

      // Intercambiar code por tokens
      const exchangeResult = await exchangeCodeForTokens(
        `${SEO_AUDIT_BASE}/facebook/exchange-code`,
        code,
        extractedClientId
      );

      if (!exchangeResult.success) {
        setStatus('error');

        let errorMsg = exchangeResult.error || 'Failed to connect Facebook';
        let timeoutDuration = 3000;

        if (errorMsg.toLowerCase().includes('unknown error')) {
          errorMsg = `${errorMsg}. This is usually caused by redirect URL mismatch, an expired one-time auth code, or page/account restrictions. Please reconnect and try once more.`;
          timeoutDuration = 6000;
        }

        if (
          exchangeResult.status === 504 ||
          exchangeResult.isNetworkError ||
          errorMsg.toLowerCase().includes('time') ||
          errorMsg.toLowerCase().includes('endpoint request timed out')
        ) {
          errorMsg =
            "Facebook is taking too long to authorize. Please close this window and click 'Connect' one more time (it will be instant).";
          timeoutDuration = 10000;
        }

        setMessage(errorMsg);

        setTimeout(() => {
          if (window.opener && !window.opener.closed) {
            window.close();
          } else {
            history.push(`/client/${extractedClientId}`);
          }
        }, timeoutDuration);
        return;
      }

      console.log('Tokens saved successfully');
      setPages(exchangeResult.data?.pages || []);
      setStatus('success');
      setMessage('Facebook connected successfully!');

      // Mostrar selector de páginas después de 1 segundo
      setTimeout(() => {
        setShowPageSelector(true);
      }, 1000);
    } catch (error) {
      console.error('Error in callback:', error);
      setStatus('error');
      setMessage('An unexpected error occurred');
      setTimeout(() => {
        if (clientId) {
          history.push(`/client/${clientId}`);
        } else {
          history.push('/');
        }
      }, 3000);
    }
  };

  const handlePageSelected = (page) => {
    // Si está en popup, enviar mensaje y cerrar
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        {
          type:
            connectionType === 'instagram'
              ? 'INSTAGRAM_OAUTH_SUCCESS'
              : 'FACEBOOK_OAUTH_SUCCESS',
          clientId
        },
        window.location.origin
      );
      window.close();
    } else {
      // Redirigir de vuelta a la página del cliente
      history.push(`/client/${clientId}`);
    }
  };

  const handleSkipPageSelection = () => {
    // Si está en popup, enviar mensaje y cerrar
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        {
          type:
            connectionType === 'instagram'
              ? 'INSTAGRAM_OAUTH_SUCCESS'
              : 'FACEBOOK_OAUTH_SUCCESS',
          clientId
        },
        window.location.origin
      );
      window.close();
    } else {
      // Redirigir de vuelta a la páginaa del cliente sin seleccionar página
      history.push(`/client/${clientId}`);
    }
  };

  if (showPageSelector) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <PageSelector
          clientId={clientId}
          initialPages={pages}
          connectionType={connectionType}
          onComplete={handlePageSelected}
          onSkip={handleSkipPageSelection}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {status === 'processing' && 'Connecting Facebook...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Connection Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-4">
            {status === 'processing' && (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            )}
            {status === 'success' && (
              <div className="text-green-600 text-5xl">✓</div>
            )}
            {status === 'error' && (
              <div className="text-red-600 text-5xl">✗</div>
            )}
            <p className="text-center text-sm text-gray-600">{message}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FacebookCallback;
